import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { renderScene, sceneMeta, FRAMES, FRAME_MS } from './scene';

// Public "Play your week" page for Hold the Yard. No login, no HQ chrome.
const TOKEN_KEY = 'agriware-yard-token';
const C = { bg: '#141B24', panel: '#1B2430', line: '#2B3640', cream: '#F4EBDD', muted: '#B8B0A2', orange: '#E8622C', good: '#6CC47A', bad: '#E0412F' };
const PIXEL = "'Press Start 2P', 'Courier New', monospace";
const BODY = "'IBM Plex Sans', Helvetica, Arial, sans-serif";

function useFonts() {
  useEffect(() => {
    const id = 'yard-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Press+Start+2P&family=IBM+Plex+Sans:wght@400;600&display=swap';
    document.head.appendChild(link);
  }, []);
}

// Keep the token out of the address bar, but survive a refresh.
function useToken() {
  const { token: urlToken } = useParams();
  const [token] = useState(() => {
    if (urlToken) { try { sessionStorage.setItem(TOKEN_KEY, urlToken); } catch {} return urlToken; }
    try { return sessionStorage.getItem(TOKEN_KEY); } catch { return null; }
  });
  useEffect(() => { if (urlToken) window.history.replaceState(null, '', '/yard'); }, [urlToken]);
  return token;
}

// Renders the 600x400 scene scaled to the available width.
function Scene({ state }) {
  const box = useRef(null);
  const [scale, setScale] = useState(1);
  const [frame, setFrame] = useState(0);
  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;
    const id = setInterval(() => setFrame(f => (f + 1) % FRAMES), FRAME_MS);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    const el = box.current;
    if (!el) return undefined;
    const fit = () => setScale(Math.min(1, el.clientWidth / 600));
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={box} style={{ width: '100%', maxWidth: 600, height: 400 * scale, margin: '0 auto', overflow: 'hidden', imageRendering: 'pixelated' }}>
      <div style={{ width: 600, height: 400, transform: `scale(${scale})`, transformOrigin: 'top left' }}
        role="img" aria-label={sceneMeta(state)?.name || state}
        dangerouslySetInnerHTML={{ __html: renderScene(state, frame) }} />
    </div>
  );
}

export default function Yard() {
  useFonts();
  const token = useToken();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!token) { setFailed(true); return; }
    fetch('/api/yard/view', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token }) })
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then(setData)
      .catch(() => setFailed(true));
  }, [token]);

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.cream, fontFamily: BODY, padding: '28px 16px 56px', boxSizing: 'border-box' }}>
      <div style={{ maxWidth: 640, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, gap: 12 }}>
          <span style={{ fontFamily: PIXEL, fontSize: 12, color: C.orange, letterSpacing: 1 }}>AGRIWARE</span>
          <span style={{ fontFamily: PIXEL, fontSize: 9, color: C.muted, letterSpacing: 1 }}>HOLD THE YARD</span>
        </div>

        {failed && (
          <Panel>
            <div style={{ fontFamily: PIXEL, fontSize: 13, lineHeight: 1.6, color: C.orange, marginBottom: 10 }}>LINK EXPIRED</div>
            <div style={{ fontSize: 15, lineHeight: 1.6, color: C.muted }}>This link is no longer available.</div>
          </Panel>
        )}
        {!failed && !data && <div style={{ fontFamily: PIXEL, fontSize: 10, color: C.muted }}>LOADING...</div>}
        {data && <Week d={data} />}
      </div>
    </div>
  );
}

function Week({ d }) {
  const meta = sceneMeta(d.state) || { name: d.state, color: C.orange };
  const base = sceneMeta(d.baseState);
  const week = Number(String(d.isoWeek).split('-W')[1]);
  const weekEnd = d.weekEnd ? new Date(d.weekEnd).toLocaleDateString('en-GB', { timeZone: 'Europe/London', day: 'numeric', month: 'long', year: 'numeric' }) : '';
  const delta = d.deltaFromLastWeek;
  const stats = [
    ['SCORE', String(d.score).padStart(4, '0')],
    ['HI-SCORE', String(d.hiScore).padStart(4, '0')],
    ['CHECKS DONE', `${Math.round(d.checkPct)}%`],
    ['FAULTS FLAGGED', String(d.faultsFlagged)],
    ['VS LAST WEEK', delta == null ? '—' : delta > 0 ? `+${delta}` : delta < 0 ? `−${Math.abs(delta)}` : '0', delta > 0 ? C.good : delta < 0 ? C.bad : C.cream],
    ...(d.streakLength >= 2 ? [['STREAK', `x${d.streakLength}`, meta.color]] : []),
  ];

  return (
    <>
      {d.preview && <div style={{ fontFamily: PIXEL, fontSize: 9, color: C.muted, marginBottom: 12 }}>TEST PREVIEW · SAMPLE NUMBERS</div>}
      <div style={{ fontSize: 14, color: C.muted, marginBottom: 6 }}>{d.orgName} · Week {week}{weekEnd ? `, ending ${weekEnd}` : ''}</div>
      <h1 style={{ fontFamily: PIXEL, fontSize: 18, lineHeight: 1.5, color: meta.color, margin: '0 0 18px', fontWeight: 400 }}>{meta.name.toUpperCase()}</h1>

      <Scene state={d.state} />

      {base && d.baseState !== d.state && (
        <div style={{ fontSize: 14, color: C.muted, marginTop: 12 }}>Special award. Underlying result: {base.name}.</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, margin: '20px 0' }}>
        {stats.map(([label, value, color]) => (
          <div key={label} style={{ background: C.panel, borderTop: `4px solid ${meta.color}`, padding: '12px 14px' }}>
            <div style={{ fontFamily: PIXEL, fontSize: 8, color: C.muted, letterSpacing: 1, marginBottom: 10 }}>{label}</div>
            <div style={{ fontFamily: PIXEL, fontSize: 18, color: color || C.cream }}>{value}</div>
          </div>
        ))}
      </div>

      {d.mostMissed?.item && (
        <p style={{ fontSize: 15, lineHeight: 1.6, margin: '0 0 16px' }}>
          Most missed this week: <strong>{d.mostMissed.item}</strong>{d.mostMissed.machine ? <> on <strong>{d.mostMissed.machine}</strong></> : null}.
        </p>
      )}

      <Panel>
        <div style={{ fontFamily: PIXEL, fontSize: 10, color: C.orange, marginBottom: 14 }}>YOUR FLEET</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ color: C.muted, textAlign: 'left' }}>
              <th style={{ fontWeight: 600, padding: '0 8px 8px 0' }}>Machine</th>
              <th style={{ fontWeight: 600, padding: '0 8px 8px', textAlign: 'right' }}>Checks</th>
              <th style={{ fontWeight: 600, padding: '0 0 8px 8px', textAlign: 'right' }}>Faults</th>
            </tr>
          </thead>
          <tbody>
            {d.perMachine.map((m, i) => {
              const short = m.expected && m.completed < m.expected;
              return (
                <tr key={i} style={{ borderTop: `1px solid ${C.line}` }}>
                  <td style={{ padding: '9px 8px 9px 0' }}>{m.label}</td>
                  <td style={{ padding: '9px 8px', textAlign: 'right', fontFamily: PIXEL, fontSize: 11, color: short ? C.bad : C.good, whiteSpace: 'nowrap' }}>{m.completed}/{m.expected}</td>
                  <td style={{ padding: '9px 0 9px 8px', textAlign: 'right', fontFamily: PIXEL, fontSize: 11 }}>{m.faults}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Panel>

      <p style={{ fontSize: 13, lineHeight: 1.6, color: C.muted, margin: '18px 0 0' }}>
        How the score works: your score starts from the share of expected checks that were done. Every fault reported adds 2 points (up to 10), and each defect left open for more than a week takes off 3 (up to 15). A perfect wave needs every check done and nothing left open. The score is that result out of 1000.
      </p>
    </>
  );
}

function Panel({ children }) {
  return <div style={{ background: C.panel, border: `1px solid ${C.line}`, padding: '18px 18px' }}>{children}</div>;
}
